import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { Settings, X } from 'lucide-react'
import { useCookieConsent, type CookiePreferences } from '@/contexts/CookieConsentContext'
import { Button } from './Button'
import { Dialog, DialogTitle, DialogDescription } from './Dialog'
import { LocalizedLink } from '@/components/LocalizedLink'

export function CookieBanner() {
  const { t } = useTranslation('common')
  const { showBanner, acceptAll, rejectAll, savePreferences } = useCookieConsent()
  const [showPreferences, setShowPreferences] = useState(false)
  const [tempPrefs, setTempPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  if (!showBanner) return null

  return (
    <>
      <AnimatePresence>
        {!showPreferences && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-20 sm:bottom-4 left-4 right-4 sm:right-auto z-[9999] sm:max-w-sm bg-white border border-gray-200 rounded-xl shadow-xl p-5"
            role="dialog"
            aria-label={t('cookie.title', 'Preferenze cookie')}
          >
            <p className="text-sm text-gray-700 mb-4">
              {t('cookie.message')}{' '}
              <LocalizedLink to="cookiePolicy" className="font-medium text-primary-600 underline hover:text-primary-700">
                {t('cookie.learnMore')}
              </LocalizedLink>
            </p>
            <div className="flex gap-2">
              <Button variant="primary" size="sm" className="flex-1" onClick={acceptAll}>
                {t('cookie.acceptAll', 'Accetta tutti')}
              </Button>
              <Button variant="outline" size="sm" className="flex-1" onClick={rejectAll}>
                {t('cookie.rejectAll', 'Rifiuta tutti')}
              </Button>
            </div>
            <button
              onClick={() => setShowPreferences(true)}
              className="mt-3 flex items-center justify-center gap-1.5 w-full text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              <Settings className="h-3.5 w-3.5" />
              {t('cookie.customize', 'Personalizza')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preferences Dialog */}
      <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
        <DialogTitle>{t('cookie.preferencesTitle', 'Preferenze Cookie')}</DialogTitle>
        <DialogDescription>{t('cookie.preferencesDescription', 'Gestisci le tue preferenze sui cookie.')}</DialogDescription>

        <div className="mt-6 space-y-4">
          {/* Necessary */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-sm text-gray-900">{t('cookie.necessary', 'Necessari')}</p>
              <p className="text-xs text-gray-500">{t('cookie.necessaryDesc', 'Essenziali per il funzionamento del sito.')}</p>
            </div>
            <div className="text-xs text-gray-400 font-medium">{t('cookie.alwaysOn', 'Sempre attivi')}</div>
          </div>

          {/* Analytics */}
          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div>
              <p className="font-medium text-sm text-gray-900">{t('cookie.analytics', 'Analitici')}</p>
              <p className="text-xs text-gray-500">{t('cookie.analyticsDesc', 'Ci aiutano a capire come usi il sito.')}</p>
            </div>
            <input
              type="checkbox"
              checked={tempPrefs.analytics}
              onChange={(e) => setTempPrefs((p) => ({ ...p, analytics: e.target.checked }))}
              className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
            />
          </label>

          {/* Marketing */}
          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div>
              <p className="font-medium text-sm text-gray-900">{t('cookie.marketing', 'Marketing')}</p>
              <p className="text-xs text-gray-500">{t('cookie.marketingDesc', 'Per contenuti e pubblicità personalizzati.')}</p>
            </div>
            <input
              type="checkbox"
              checked={tempPrefs.marketing}
              onChange={(e) => setTempPrefs((p) => ({ ...p, marketing: e.target.checked }))}
              className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
            />
          </label>
        </div>

        <div className="mt-6 flex gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={() => setShowPreferences(false)}>
            <X className="h-4 w-4" />
            {t('cookie.cancel', 'Annulla')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              savePreferences(tempPrefs)
              setShowPreferences(false)
            }}
          >
            {t('cookie.savePreferences', 'Salva preferenze')}
          </Button>
        </div>
      </Dialog>
    </>
  )
}
